import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatMenuTrigger } from '@angular/material/menu'
import { MatProgressBar } from '@angular/material/progress-bar'
import {
  ApiService,
  AppointmentModel,
  AudioConfig,
  AudioService,
  AuthService,
  CANNOT_TEXT_REASON,
  Clinic,
  ClinicService,
  getCannotTextReason,
  MessageHistoryRecord,
  MessageHistoryRecordModel,
  MessageTemplate,
  MessageTemplateModel,
  Patient,
  PatientPalRestError,
  PermissionsService,
  ProgressIndicatorService,
  WebsocketAction,
  WebSocketService,
} from '@mmx/shared'
import { get, last, orderBy, remove, uniqBy } from 'lodash'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import {
  combineLatestWith,
  debounceTime,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators'

import { EmailPreviewDialogComponent } from '../email-preview-dialog/email-preview-dialog.component'

@Component({
  selector: 'mmx-patient-messages-card',
  templateUrl: './messages-card.component.html',
  styleUrls: ['./messages-card.component.scss'],
  standalone: false,
})
export class PatientMessagesCardComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild(MatProgressBar, { static: true }) progressBar: MatProgressBar
  @ViewChild('messageHistory') messageHistory: ElementRef
  @ViewChild('messageBody') messageBody: ElementRef
  @ViewChild(MatMenuTrigger) contextMenu: MatMenuTrigger

  @Input() patient: Patient
  @Input() appointment?: AppointmentModel
  @Input() expanded: boolean
  @Input() embedded = false
  @Input() title?: string

  public clinic: Clinic
  public direction = MessageHistoryRecord.DIRECTION
  public loading$ = new BehaviorSubject(false)
  public sending$ = new BehaviorSubject(false)
  public translating$ = new BehaviorSubject(false)
  public isInternalMessage = false
  public translationError = false
  // public messages: MessageHistoryRecordModel[] = []
  public translatedText: string
  public typings: { clinician: { id: string; name: string }; text: string }[] =
    []
  public isLoadingTemplates = false
  public templates: MessageTemplateModel[] = null
  public contextualMessage?: MessageHistoryRecordModel

  private onDestroy = new Subject<void>()
  private scroll$ = new Subject<void>()

  private get appointmentId(): string | undefined {
    const pathSegments = location.pathname.split('/')
    const appointmentSegment = pathSegments.findIndex(
      (s) => s === 'appointment',
    )
    const appointmentId =
      appointmentSegment > 0 ? pathSegments[appointmentSegment + 1] : undefined

    return appointmentId ?? this.appointment?.id
  }

  form = this.fb.group({
    messageBody: this.fb.control<string>(null, Validators.required),
  })

  private patient$ = new BehaviorSubject<Patient>(null)

  private patientChanged$ = this.patient$.pipe(
    filter(Boolean),
    distinctUntilKeyChanged('id'),
  )

  canTextArbitrary$ = this.permissionsService
    .hasPermission('message.arbitrary')
    .pipe(takeUntil(this.onDestroy), shareReplay(1))

  canText$ = this.patientChanged$.pipe(
    combineLatestWith(this.canTextArbitrary$),
    map(([patient, allowedToMessage]) => patient.canText && allowedToMessage),
    distinctUntilChanged(),
  )

  CANNOT_TEXT_REASON = CANNOT_TEXT_REASON
  cannotTextReason$: Observable<CANNOT_TEXT_REASON | undefined> =
    this.patientChanged$.pipe(map((patient) => getCannotTextReason(patient)))

  enabled$ = this.canText$.pipe(
    combineLatestWith(this.sending$),
    map(([canText, isSending]) => canText && !isSending),
    distinctUntilChanged(),
  )

  refresh$ = new Subject<void>()

  progress$: Observable<boolean> = this.loading$.pipe(
    combineLatestWith(this.sending$, this.translating$),
    map(([loading, sending, translating]) => loading || sending || translating),
    distinctUntilChanged(),
  )

  private wsReceivedMessages$ = new BehaviorSubject<
    MessageHistoryRecordModel[]
  >([])

  messages$ = this.patientChanged$.pipe(
    combineLatestWith(this.refresh$.pipe(startWith(null))),
    debounceTime(10),
    tap(() => this.loading$.next(true)),
    switchMap(([patient]) =>
      this.restService.getMessageHistory({ patientId: patient.id }),
    ),
    combineLatestWith(this.wsReceivedMessages$),
    map(([resp, receivedMessages]) =>
      orderBy(uniqBy([...resp.data, ...receivedMessages], 'id'), 'createdAt'),
    ),
    tap(() => {
      this.loading$.next(false)
      this.scroll$.next()
    }),
  )

  private ws$ = this.ws.patientMessage$.pipe(
    takeUntil(this.onDestroy),
    filter(
      (message) =>
        message.action === WebsocketAction.MESSAGING_NEW_MESSAGE ||
        message.action === WebsocketAction.MESSAGING_TYPING,
    ),
    tap((message) => {
      // eslint-disable-next-line no-console
      console.log('Message received', message)

      if (message.action === WebsocketAction.MESSAGING_NEW_MESSAGE) {
        this.addMessage(new MessageHistoryRecordModel(message.data))
        this.audioService.play(AudioConfig.EVENT.NEW_MESSAGE)
      } else if (message.action === WebsocketAction.MESSAGING_TYPING) {
        if (message.data.clinicianId) {
          remove(
            this.typings,
            (t) => t.clinician.id === message.data.clinicianId,
          )
        } else if (message.data.clinician?.id) {
          remove(
            this.typings,
            (t) => t.clinician.id === message.data.clinician.id,
          )
        }

        if (
          message.data.clinician.id !== this.authService.user.id &&
          message.data.text
        ) {
          this.typings.push(message.data)
        }
      }
    }),
  )

  constructor(
    private clinicService: ClinicService,
    private restService: ApiService,
    private authService: AuthService,
    private ws: WebSocketService,
    private progressService: ProgressIndicatorService,
    private audioService: AudioService,
    private permissionsService: PermissionsService,
    private fb: FormBuilder,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.clinic = this.clinicService.clinic

    this.form.controls.messageBody.valueChanges
      .pipe(
        takeUntil(this.onDestroy),
        debounceTime(300),
        distinctUntilChanged(),
        filter((text) => text != null && text.length > 2),
      )
      .subscribe((text) => {
        this.ws.messageQueue.next({
          action: WebsocketAction.MESSAGING_TYPING,
          patient: this.patient.id,
          text,
        })
      })

    if (this.patient) {
      this.patient$.next(this.patient)
    }

    this.enabled$
      .pipe(
        filter(() => Boolean(this.form)),
        tap((enabled) => {
          if (!enabled) {
            this.form.controls.messageBody.disable()
          } else {
            this.form.controls.messageBody.enable()
          }
        }),
      )
      .subscribe()

    this.ws$.subscribe()

    this.scroll$
      .pipe(
        takeUntil(this.onDestroy),
        debounceTime(300),
        tap(() => this.scrollDown()),
      )
      .subscribe()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.patient) {
      this.patient$.next(changes.patient.currentValue)
    }
  }

  ngOnDestroy() {
    this.onDestroy.next()
    this.onDestroy.complete()
  }

  openWindow() {
    if (this.patient) {
      const win = window.open(
        `/popup/patients/${this.patient.id}/messages`,
        '_blank',
        'width=500, height=500',
      )

      if (win == null) {
        this.progressService.snack({
          message: 'A popup blocked prevented the window from expanding',
          panelClass: 'warn',
        })
      }
    }
  }

  onContextMenu(event: Event, message: MessageHistoryRecordModel): void {
    event.preventDefault()
    this.contextualMessage = message
    // this.contextMenu.focus('mouse')
    this.contextMenu.openMenu()
  }

  isImage(src: string) {
    const extension = last<string>(src.split('?')[0].split('.'))
    return ['gif', 'jpeg', 'jpg', 'png', 'webp'].includes(extension)
  }

  templateMenuOpened() {
    if (this.isLoadingTemplates || this.templates != null) {
      return
    }

    this.isLoadingTemplates = true

    this.restService
      .getMessageTemplates({
        includeWorkflowTemplates: false,
        includeSystemTemplates: false,
      })
      .subscribe((templates) => {
        this.templates = templates || []
        this.isLoadingTemplates = false
      })
  }

  onTemplateMessageSelected(template: string) {
    if (template) {
      this.form.controls.messageBody.setValue(template)
      this.form.controls.messageBody.updateValueAndValidity()
    }
  }

  translate(text: string = this.form.value.messageBody) {
    if (this.form.invalid || !text) {
      this.form.markAllAsTouched()
      return
    }

    this.translating$.next(true)
    this.translationError = false
    this.restService
      .translate({
        input: text,
        inputLanguage: 'en',
        outputLanguage: this.patient.language,
      })
      .subscribe({
        next: (resp) => {
          this.translating$.next(false)
          if (resp.data.length > 0) {
            this.translatedText = resp.data[0].translatedText
          } else {
            this.translationError = true
          }
          this.scroll$.next()
        },
        error: () => {
          this.translating$.next(false)
          this.translationError = true
        },
      })
  }

  useTranslation() {
    this.form.patchValue({
      messageBody: this.translatedText,
    })
    this.translatedText = null
    this.form.updateValueAndValidity()
  }

  sendMessage(sendTranslation = false) {
    if (this.form.invalid) {
      return
    }

    this.sending$.next(true)
    this.restService
      .sendPatientMessage(this.patient.id, {
        type: MessageTemplate.TYPE.CUSTOM,
        method: 'sms',
        data: this.form.value.messageBody,
        translatedText:
          sendTranslation && this.translatedText
            ? this.translatedText
            : undefined,
        appointmentId: this.appointmentId,
        internal: this.isInternalMessage,
      })
      .subscribe({
        next: (resp) => {
          this.sending$.next(false)
          this.translating$.next(false)
          this.addMessage(
            new MessageHistoryRecordModel({
              clinician: this.authService.user,
              ...resp.data,
            }),
          )
          this.clear()
        },
        error: (err: PatientPalRestError) => {
          this.sending$.next(false)
          const errorMessage =
            get(err, 'error.errors[0].message') ||
            err.error.message ||
            'Please try again.'
          this.progressService.snack({
            message: `Failed to send message. ${errorMessage}`,
            panelClass: 'warn',
          })
        },
      })
  }

  private addMessage(message: MessageHistoryRecordModel) {
    const messages = this.wsReceivedMessages$.getValue() ?? []
    messages.push(message)
    this.wsReceivedMessages$.next(messages)
  }

  private clear() {
    this.form.reset()
    this.translatedText = ''
    this.scroll$.next()
  }

  private scrollDown() {
    requestAnimationFrame(() => {
      if (this.messageHistory) {
        const el = this.messageHistory.nativeElement
        el.scrollTop = el.scrollHeight - el.clientHeight + 100
      }
    })
  }

  extractFirstLine(text: string): string {
    return text?.split('\n')[0] || ''
  }

  openEmailPreview(message: any): void {
    this.dialog.open(EmailPreviewDialogComponent, {
      width: '40%',
      data: { emailContent: message.html },
    })
  }

  refreshMedia(message: MessageHistoryRecordModel) {
    return this.restService.updateMessageMediaUrls(message.id)
      .subscribe(updatedMessage => {
        message.mediaObjects = updatedMessage.mediaObjects
      })
  }
}
